import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import Modal from 'react-native-modal';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { Reservation } from '@/api';
import { useDeleteReservation } from '@/api/partners/reservations/use-delete-reservation';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { Button, Text } from '@/ui';

interface DeleteBlockModalProps {
  visible?: boolean;
  trip: Reservation;
  onClose: () => void;
  onSuccess: () => void;
  style?: any;
}

export function DeleteBlockModal({
  visible = false,
  trip,
  onClose,
  onSuccess,
  style,
}: DeleteBlockModalProps) {
  const { styles, theme } = useStyles(stylesheet);
  const [showModal, setShowModal] = useState(visible);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const { mutateAsync: deleteReservation, isPending: isDeletingReservation } =
    useDeleteReservation();

  useEffect(() => {
    setShowModal(visible);
  }, [visible]);

  const handleDeleteReservation = async () => {
    try {
      await deleteReservation({
        id: trip.id,
      });

      onSuccess();
    } catch (e: any) {
      setErrorMessage(e.message);
    }
  };

  return (
    <Modal
      animationIn="slideInUp"
      animationInTiming={300}
      animationOut="slideOutDown"
      animationOutTiming={300}
      isVisible={showModal}
      style={styles.modal}
      hasBackdrop
      onBackdropPress={() => {
        setShowModal(false);
        onClose?.();
      }}
    >
      <View
        style={{
          ...styles.container,
          ...style,
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            padding: 16,
            paddingBottom: 18,
          }}
        >
          <View style={{ gap: 8 }}>
            <Text type="title2">Delete Admin Block</Text>
          </View>

          <NavigationButton
            type={NavigationButtonTypes.cancel}
            onPress={() => {
              setShowModal(false);
              onClose?.();
            }}
            style={{
              position: 'absolute',
              right: 16,
              top: 16,
            }}
          />
        </View>

        <View style={styles.separator} />

        <View
          style={{
            paddingHorizontal: 16,
            paddingVertical: 20,
          }}
        >
          <Text type="bodyRegular">
            Are you sure you want to delete this admin block? Your car will
            become available for bookings.
          </Text>
        </View>

        <View style={styles.separator} />

        <View
          style={{
            flexDirection: 'column',
            alignItems: 'flex-end',
            paddingHorizontal: 16,
            paddingTop: 16,
            paddingBottom: 20,
            gap: 24,
          }}
        >
          <View style={styles.buttonRow}>
            <Button
              text="Cancel"
              type="ghost"
              onPress={() => onClose()}
              style={styles.button}
            />

            <Button
              text="Delete"
              type="primary"
              onPress={handleDeleteReservation}
              style={styles.button}
              loading={isDeletingReservation}
              disabled={isDeletingReservation}
            />
          </View>

          {errorMessage ? (
            <Text type="caption1" style={{ color: theme.colors.error }}>
              Error: {errorMessage}
            </Text>
          ) : null}
        </View>
      </View>
    </Modal>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  modal: {
    margin: {
      xs: 16,
      md: 0,
    },
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    width: '100%',
    maxWidth: {
      xs: 520,
      sm: 600,
    },
    backgroundColor: theme.colors.dialog.backgroundColor,
    borderRadius: 16,
  },
  button: {
    width: {
      xs: '100%',
      md: 150,
    },
  },
  buttonRow: {
    flexDirection: {
      xs: 'column-reverse',
      md: 'row',
    },
    justifyContent: {
      xs: 'flex-start',
      md: 'flex-end',
    },
    gap: 8,
    width: '100%',
  },
  separator: {
    height: 1,
    backgroundColor: theme.colors.card.default.borderColor,
  },
}));
